/**
 * "Has the purchaser decided this line's unit?" — and the serial a new line takes.
 *
 * ---- The unit question (Akhil, 2026-09-15) ----
 *
 * A customer asks in pieces; we sell the item by the bag or the carton. Picking a unit for them
 * — which `uomAfterItemChange` does, because `uom` is MANDATORY on the server and a line cannot
 * be saved without one — silently changes what the line means. So the stored unit is treated as
 * PROVISIONAL whenever the customer named a unit the item is not sold in, or the line sits on a
 * unit the item does not have (the item was changed): the row says so, offers the item's units
 * and a request for the customer's, and RFQ send / quotation create refuse until every such
 * line is decided.
 *
 * `customer_uom` is the customer's unit AS WRITTEN — recorded once at create for every line and
 * never changed (Akhil, 2026-09-15: "it will not change"). It is read here as the fact to decide
 * against, never as the decision.
 *
 * "Decided" is mostly a property of the VALUE: only the item's own units are offered, so a line
 * on one of them was either matched to it or chosen. The one case that cannot be read off the
 * line — the customer asked in a unit the item does not have and the line already sits on one it
 * does — used to be confirmed per session and asked again after a reload (Akhil, 2026-09-18:
 * the purchaser went to the quotation page, came back, and was asked again). It is now RECORDED
 * on the line's `note` (`uomConfirmationNote`), the one free writable field `Enquiry Item` has —
 * measured 2026-09-18; the server never fills it and no RFQ prints it — keyed to the item, so a
 * changed item reopens the question. Ask `E21` is for a real `uom_confirmed` flag.
 * (`qty_as_per_stock_uom` was tried first and withdrawn: the server fills it on save.)
 */

import { customerUomHint, sameUnit } from './customerUom'
import { matchItemUom } from './packSize'
import type { EnquiryItem, Item } from './types'

const sameSpelling = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()

export interface UomChoice {
  /** As the customer wrote it. */
  customerUnit: string
  /** One sentence for the row. */
  reason: string
}

export function uomNeedsChoice(
  line: Pick<EnquiryItem, 'raw_text' | 'uom' | 'item_code' | 'customer_uom'> & { note?: string | null },
  item: Pick<Item, 'base_uom' | 'uom_conversions'> | undefined,
  /**
   * `exact`: the customer's unit counts as one of the item's only when the item literally sells
   * in it — PCS is not Nos here. For a line whose item the PURCHASER changed (Akhil,
   * 2026-09-17): the equivalence decides the original match, but a different item that does not
   * carry the customer's unit is theirs to confirm. Off by default, so the 2026-09-16 rule
   * ("auto select if nos = pcs, and show it in the warning") still holds elsewhere.
   */
  options: { exact?: boolean } = {},
): UomChoice | null {
  if (!item || !line.item_code) return null
  // A person confirmed this unit for this item — recorded on the line, so it survives a reload.
  if (isUomConfirmed(line)) return null
  const recorded = line.customer_uom.trim()
  const same = options.exact ? sameSpelling : sameUnit
  const hint = recorded
    ? { uom: recorded, differs: !same(recorded, line.uom) }
    : customerUomHint(line.raw_text, line.uom)
  const accepted = [...new Set([item.base_uom, ...item.uom_conversions.map((c) => c.uom)])].filter((u) => u !== '')
  const units = accepted.join(' or ')
  const inItem = (unit: string) => (options.exact ? (accepted.find((u) => sameSpelling(u, unit)) ?? null) : matchItemUom(unit, accepted))
  // A unit the item is not sold in is undecided whatever the customer said — or did not say.
  if (matchItemUom(line.uom, accepted) === null) {
    const customerUnit = hint?.uom ?? ''
    return {
      customerUnit,
      reason: customerUnit
        ? `the customer asked in ${customerUnit}, which this item is not sold in — choose ${units}, or request ${customerUnit}`
        : `the line is on ${line.uom}, which this item is not sold in — choose ${units}`,
    }
  }
  if (!hint) return null
  const inMaster = inItem(hint.uom)
  // The customer's unit IS one of the item's — by name or by equivalence (PCS is Nos) — and the
  // line is on it: nothing to decide. `unitEquivalenceNote` tells the purchaser when it was the
  // equivalence that decided it.
  if (inMaster !== null && (inMaster === line.uom || !hint.differs)) return null
  if (inMaster !== null && hint.differs) {
    return { customerUnit: hint.uom, reason: `the customer asked in ${hint.uom}; the line is on ${line.uom} — confirm which` }
  }
  return {
    customerUnit: hint.uom,
    reason: `the customer asked in ${hint.uom}, which this item is not sold in — choose ${units}, or request ${hint.uom}`,
  }
}

/**
 * "customer asked in PCS — taken as Nos, the same unit": said on the row whenever the line's unit
 * is the item's own spelling of what the customer wrote rather than the word itself (Akhil,
 * 2026-09-16). `null` when the words already match, or when they are genuinely different units
 * — that case is `uomNeedsChoice`'s.
 */
export function unitEquivalenceNote(
  line: Pick<EnquiryItem, 'raw_text' | 'uom' | 'customer_uom'>,
  item: Pick<Item, 'base_uom' | 'uom_conversions'> | undefined,
): string | null {
  if (!item) return null
  const recorded = line.customer_uom.trim()
  const customerUnit = recorded || customerUomHint(line.raw_text, line.uom)?.uom || ''
  if (customerUnit === '' || sameUnit(customerUnit, line.uom)) return null
  const accepted = [...new Set([item.base_uom, ...item.uom_conversions.map((c) => c.uom)])].filter((u) => u !== '')
  return matchItemUom(customerUnit, accepted) === line.uom ? `customer asked in ${customerUnit} — taken as ${line.uom}, the same unit` : null
}

/**
 * The number an added line takes: the lowest one missing from the sequence — a deleted line's
 * number comes back into use (Akhil, 2026-09-15) — else one past the highest.
 */
export function nextSerial(lines: readonly Pick<EnquiryItem, 'cust_sno'>[]): string {
  return freeSerials(lines.map((l) => l.cust_sno), [])[0]
}

/**
 * Every free number, ascending: the gaps in 1…max first, then max + 1. `taken` are numbers
 * already promised to other adds in the same round.
 */
export function freeSerials(serials: readonly string[], taken: readonly string[]): string[] {
  const used = new Set<number>()
  for (const s of serials) if (/^\d+$/.test(s.trim())) used.add(Number(s))
  for (const t of taken) if (/^\d+$/.test(t.trim())) used.add(Number(t))
  const max = used.size ? Math.max(...used) : 0
  const out: string[] = []
  for (let n = 1; n <= max; n++) if (!used.has(n)) out.push(String(n))
  out.push(String(max + 1))
  return out
}

/**
 * The chips the row offers when the unit is undecided — the customer's own unit FIRST, named as
 * theirs (Akhil, 2026-09-17: it was listed eleventh of twenty), then the item's units in the
 * item's order, "keep" on the one the line already has.
 */
export function unitChips(
  item: Pick<Item, 'base_uom' | 'uom_conversions'>,
  lineUom: string,
  customerUnit: string,
): Array<{ uom: string; label: string; customers: boolean }> {
  const accepted = [...new Set([item.base_uom, ...item.uom_conversions.map((c) => c.uom)])].filter((u) => u !== '')
  const theirs = customerUnit ? matchItemUom(customerUnit, accepted) : null
  const chip = (u: string) => ({
    uom: u,
    label: u === lineUom ? `keep ${u}` : u === theirs ? `${u} · customer’s` : u,
    customers: u === theirs,
  })
  if (!theirs || theirs === lineUom) return accepted.map(chip)
  return [chip(theirs), ...accepted.filter((u) => u !== theirs).map(chip)]
}

/** The note a unit decision writes — see the module header. */
export function uomConfirmationNote(uom: string, itemCode: string, customerUnit: string): string {
  const base = `Unit confirmed: ${uom} for ${itemCode}`
  return customerUnit.trim() ? `${base} (customer asked in ${customerUnit.trim()})` : base
}

/** Whether the line's note records a confirmation of ITS unit for ITS item. */
export function isUomConfirmed(line: { note?: string | null; uom: string; item_code: string | null }): boolean {
  const m = /^Unit confirmed: (\S+) for (\S+)/.exec((line.note ?? '').trim())
  return !!m && m[1] === line.uom && m[2] === (line.item_code ?? '')
}
